import { useMemo, useState } from "react";
import {
  PageShell,
  PageHeader,
  ListView,
  ListToolbar,
  ListSkeleton,
  EmptyState,
  Modal,
} from "../../../Components/shared";
import { Pagination } from "../../../Components/atoms";
import { useAlert } from "../../../context/AlertContext";
import RowActionButton from "../../../Components/factory/storageRoles/RowActionButton";
import MetadataCell from "../../../Components/factory/storageRoles/MetadataCell";
import {
  FaBoxOpen,
  FaVial,
  FaCheckCircle,
  FaCog,
  FaUnlink,
  FaLayerGroup,
} from "react-icons/fa";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import { useDebounce } from "../../../Hooks/useDebounce";
import usePersistedFilters from "../../../Hooks/usePersistedFilters";
import {
  useGetFactoryPartsQuery,
  useGetFactoryPartUnitsQuery,
  useDeleteFactoryPartUnitMutation,
} from "../../../store";

const STATUS_FILTERS = [
  { id: "all", label: "Tümü", icon: FaLayerGroup },
  { id: "uretim", label: "Üretimde", icon: FaVial },
  { id: "hazir", label: "Hazır", icon: FaCheckCircle },
  { id: "kullanildi", label: "Kullanıldı", icon: FaCog },
  { id: "bakim", label: "Bakımda", icon: FaUnlink },
];

const STATUS_BADGE = {
  uretim: "bg-amber-50 text-amber-700 border-amber-100",
  hazir: "bg-emerald-50 text-emerald-700 border-emerald-100",
  kullanildi: "bg-indigo-50 text-indigo-700 border-indigo-100",
  bakim: "bg-red-50 text-red-700 border-red-100",
};

const STATUS_LABEL = {
  uretim: "Üretimde",
  hazir: "Hazır",
  kullanildi: "Kullanıldı",
  bakim: "Bakımda",
};

const TABLE_HEADERS = [
  { label: "SERİ NO", className: "w-[18%]" },
  { label: "PARÇA", className: "w-[18%]" },
  { label: "DURUM", className: "w-[12%]" },
  { label: "ÜST PARÇA", className: "w-[14%]" },
  { label: "KİMLİK", className: "w-[14%]" },
  { label: "BÖLGE", className: "w-[9%]" },
  { label: "ÜRETİM", className: "w-[12%]" },
  { label: "İŞLEM", className: "w-[11%] text-right" },
];

const formatDate = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Fabrika Parçalar — katalog özeti + seri no'lu ünite envanteri. */
export default function FactoryPartsPage() {
  const { auth } = useAuth();
  const token = auth?.accessToken;

  const [filters, updateFilters] = usePersistedFilters("factoryParts:filters", {
    status: "all",
    search: "",
  });
  const [page, setPage] = useState(1);

  // Durum adları değişti (test→uretim, sokuldu→bakim): sessionStorage'da
  // kalan eski filtre boş liste gösterirdi — tanınmayan değeri "Tümü"ye çevir.
  const statusFilter = STATUS_FILTERS.some((f) => f.id === filters.status)
    ? filters.status
    : "all";

  const debouncedSearch = useDebounce(filters.search, 500);
  const isDebouncing = filters.search !== debouncedSearch;

  const { data: catalog, isLoading: catalogLoading } = useGetFactoryPartsQuery(
    { token },
    { skip: !token }
  );

  const {
    data: unitsData,
    isLoading: unitsLoading,
    isFetching: unitsFetching,
  } = useGetFactoryPartUnitsQuery(
    {
      token,
      status: statusFilter === "all" ? undefined : statusFilter,
      search: debouncedSearch || undefined,
      page,
      limit: 25,
    },
    { skip: !token }
  );

  const loading = unitsLoading || unitsFetching || isDebouncing;
  const units = unitsData?.items || [];
  const totalCount = unitsData?.totalCount || 0;
  const totalPages = Math.max(Math.ceil(totalCount / 25), 1);

  const { showSuccess, showError } = useAlert();
  const { hasPermission } = usePermission();
  const canManageUnits = hasPermission("factory.parts.manage");
  const [deleteUnit, { isLoading: deleting }] = useDeleteFactoryPartUnitMutation();
  const [deleteTarget, setDeleteTarget] = useState(null);

  const submitDeleteUnit = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUnit({ token, id: deleteTarget._id }).unwrap();
      showSuccess(`${deleteTarget.serialNo} silindi`);
      setDeleteTarget(null);
    } catch (err) {
      showError(err?.data?.message || "Parça silinemedi");
    }
  };

  const partSummary = useMemo(() => {
    const parts = catalog?.parts || [];
    return parts.map((p) => ({
      ...p,
      hazir: p.unitCounts?.hazir || 0,
      uretim: p.unitCounts?.uretim || 0,
      kullanildi: p.unitCounts?.kullanildi || 0,
    }));
  }, [catalog]);

  return (
    <PageShell>
      <div className="px-3 py-2.5 sm:p-4 md:p-6 border-b border-gray-100 shrink-0">
        <PageHeader
          icon={FaBoxOpen}
          iconColor="emerald"
          title="Fabrika Parçaları"
          subtitle="Parça kataloğu ve seri no'lu ünite envanteri"
          stats={[{ label: "Toplam Ünite", value: catalog?.totalUnitCount ?? 0 }]}
          isLoading={catalogLoading}
          mobileSlotId="factory-parts-mobile-slot"
        />
        {/* Katalog özeti — parça tipi başına stok */}
        {partSummary.length > 0 && (
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
            {partSummary.map((p) => (
              <div
                key={p._id}
                className="shrink-0 bg-white border border-gray-200 rounded-xl px-3 py-2 min-w-[150px]"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-gray-900">
                    {p.name}
                  </span>
                  <span className="font-mono text-[10px] text-gray-400">
                    {p.code}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[11px]">
                  <span className="text-emerald-700 font-semibold">
                    {p.hazir} hazır
                  </span>
                  <span className="text-amber-600">{p.uretim} üretimde</span>
                  <span className="text-indigo-600">{p.kullanildi} takılı</span>
                </div>
                {p.subParts?.length > 0 && (
                  <div className="mt-1 text-[10px] text-gray-400 truncate">
                    ⊃ {p.subParts.map((s) => s.name).join(", ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ListToolbar
        mobileFilterSlotId="factory-parts-mobile-slot"
        filterTabs={{
          filters: STATUS_FILTERS,
          activeFilter: statusFilter,
          onChange: (id) => {
            updateFilters({ status: id });
            setPage(1);
          },
          totalCount,
          isLoading: loading,
          color: "emerald",
        }}
        searchFields={[
          {
            placeholder: "Seri no ara...",
            value: filters.search,
            onChange: (v) => {
              updateFilters({ search: v });
              setPage(1);
            },
          },
        ]}
        searchNote={isDebouncing ? "Aranıyor..." : null}
      />

      <ListView
        headers={TABLE_HEADERS}
        tableClassName="table-fixed"
        data={units}
        isLoading={loading}
        loadingNode={
          <ListSkeleton
            columns={TABLE_HEADERS.map((h) => ({ label: h.label }))}
            rowCount={15}
            message="Parça üniteleri yükleniyor..."
          />
        }
        empty={
          <EmptyState
            message="Parça ünitesi bulunamadı"
            suggestion="Filtreleri değiştirmeyi deneyin"
          />
        }
        footer={
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={25}
            onPageChange={setPage}
            disabled={loading}
          />
        }
        renderRow={(u) => (
          <tr key={u._id} className="border-b border-gray-100 hover:bg-gray-50">
            <td className="px-4 py-3 font-mono text-sm font-semibold text-gray-900 whitespace-nowrap">
              {u.serialNo}
            </td>
            <td className="px-4 py-3 text-sm text-gray-700 truncate">{u.partName}</td>
            <td className="px-4 py-3">
              <span
                className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_BADGE[u.status] || "bg-gray-50 text-gray-600 border-gray-100"}`}
              >
                {STATUS_LABEL[u.status] || u.status}
              </span>
            </td>
            <td className="px-4 py-3 font-mono text-xs text-gray-500">
              {u.parentUnit?.serialNo || "-"}
            </td>
            <td className="px-4 py-3">
              <MetadataCell metadata={u.metadata} />
            </td>
            <td className="px-4 py-3 text-sm text-gray-500">
              {u.zone?.name || "-"}
            </td>
            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
              {formatDate(u.producedAt)}
            </td>
            <td className="px-4 py-3">
              <div className="flex justify-end">
                {/* Makinede takılı parça silinemez — önce bakımda sökülmeli */}
                {canManageUnits && u.status !== "kullanildi" && (
                  <RowActionButton
                    variant="danger"
                    onClick={() => setDeleteTarget(u)}
                  >
                    Sil
                  </RowActionButton>
                )}
              </div>
            </td>
          </tr>
        )}
        renderMobileCard={(u) => (
          <div
            key={u._id}
            className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col gap-1"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm font-semibold text-gray-900">
                {u.serialNo}
              </span>
              <span
                className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_BADGE[u.status] || "bg-gray-50 text-gray-600 border-gray-100"}`}
              >
                {STATUS_LABEL[u.status] || u.status}
              </span>
            </div>
            <div className="text-xs text-gray-600">{u.partName}</div>
            <MetadataCell metadata={u.metadata} compact />
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-400">
                {formatDate(u.producedAt)}
              </span>
              {canManageUnits && u.status !== "kullanildi" && (
                <RowActionButton
                  variant="danger"
                  onClick={() => setDeleteTarget(u)}
                >
                  Sil
                </RowActionButton>
              )}
            </div>
          </div>
        )}
      />

      {/* Parça ünitesi silme onayı */}
      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        maxWidth="md"
      >
        <Modal.Header
          title="Parça Ünitesini Sil"
          subtitle={`${deleteTarget?.serialNo || ""} · ${deleteTarget?.partName || ""}`}
          onClose={() => setDeleteTarget(null)}
        />
        <Modal.Body>
          <p className="text-sm text-gray-600">
            Bu parça ünitesi kalıcı olarak silinecek. Bağlı alt parçalar
            silinmez, yalnız bağları çözülür ve tekrar boşta kalır. Bu işlem
            geri alınamaz.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={() => setDeleteTarget(null)}
            className="px-3.5 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Vazgeç
          </button>
          <button
            onClick={submitDeleteUnit}
            disabled={deleting}
            className="px-3.5 py-2 text-sm font-semibold text-white bg-fifth rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {deleting ? "Siliniyor..." : "Kalıcı Olarak Sil"}
          </button>
        </Modal.Footer>
      </Modal>
    </PageShell>
  );
}
